import { AgentResult, CompanyMemory, Project } from '../types';
import { AuthService } from './authService';

interface WorkspaceSnapshotPayload {
  project: Project;
  results: AgentResult[];
  memories: Record<string, CompanyMemory>;
}

const WORKSPACE_SYNC_BASE = '/sync/workspaces';

export class WorkspaceSyncService {
  static async loadWorkspaces(): Promise<WorkspaceSnapshotPayload[]> {
    const response = await AuthService.authFetch(WORKSPACE_SYNC_BASE);
    if (!response.ok) {
      throw new Error(`Workspace sync load failed: ${response.status}`);
    }

    const data = await response.json();
    return Array.isArray(data.workspaces) ? data.workspaces : [];
  }

  static async saveWorkspace(
    project: Project,
    results: AgentResult[],
    memories: Record<string, CompanyMemory>
  ): Promise<void> {
    const response = await AuthService.authFetch(`${WORKSPACE_SYNC_BASE}/${project.id}`, {
      method: 'PUT',
      headers: {
        ...Object.fromEntries(AuthService.authHeaders(true).entries()),
      },
      body: JSON.stringify({
        project,
        results,
        memories,
      }),
    });

    if (!response.ok) {
      throw new Error(`Workspace sync save failed: ${response.status}`);
    }
  }

  static async deleteWorkspace(projectId: string): Promise<void> {
    const response = await AuthService.authFetch(`${WORKSPACE_SYNC_BASE}/${projectId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Workspace sync delete failed: ${response.status}`);
    }
  }

  static async loadMemory(projectId: string, companyId: string): Promise<CompanyMemory | null> {
    const response = await AuthService.authFetch(`${WORKSPACE_SYNC_BASE}/${projectId}/memories/${companyId}`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Workspace memory load failed: ${response.status}`);
    }

    const data = await response.json();
    return data.memory || null;
  }

  static async saveMemory(projectId: string, companyId: string, memory: CompanyMemory): Promise<void> {
    const response = await AuthService.authFetch(`${WORKSPACE_SYNC_BASE}/${projectId}/memories/${companyId}`, {
      method: 'PUT',
      headers: {
        ...Object.fromEntries(AuthService.authHeaders(true).entries()),
      },
      body: JSON.stringify(memory),
    });

    if (!response.ok) {
      throw new Error(`Workspace memory save failed: ${response.status}`);
    }
  }
}
